import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';

const OrderPrintThermal = ({ order, customer, showItemRate = true }) => {
  const [termsAndConditions, setTermsAndConditions] = useState([]);

  useEffect(() => {
    loadTermsAndConditions();
  }, []);

  const loadTermsAndConditions = async () => {
    try {
      const res = await axiosInstance.get('/terms-conditions/type/orders');
      // Process terms: split content by lines for bullet points
      const processedTerms = [];
      (res.data || []).forEach(term => {
        if (term.is_active && term.content) {
          const lines = term.content.split('\n').filter(line => line.trim());
          processedTerms.push(...lines);
        }
      });
      setTermsAndConditions(processedTerms);
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!order || !order.items) {
      return { subtotal: 0, totalAdvance: 0, metalCredit: 0 };
    }

    let subtotal = 0;
    order.items?.forEach(item => {
      const weight = item.weight_type === 'Fixed' 
        ? parseFloat(item.weight_fixed || 0) 
        : (parseFloat(item.weight_min || 0) + parseFloat(item.weight_max || 0)) / 2;
      
      const rate = parseFloat(item.rate || 0);
      const making = parseFloat(item.making_value || 0);
      const quantity = parseFloat(item.quantity || 1);
      
      let itemTotal;
      if (item.making_type === 'WASTAGE') {
        let rate24k = parseFloat(order.base24k_rate_ex_gst || 0);
        if (order.gst_input_style === 'EXCL' && order.tax_scope !== 'KACCHA' && rate24k > 0) {
          rate24k = rate24k * 1.03;
        }
        itemTotal = weight * rate24k * (making / 100);
      } else {
        itemTotal = weight * rate * quantity;
        if (item.making_type === 'PERCENT') {
          itemTotal += itemTotal * (making / 100);
        } else if (item.making_type === 'PER_GRAM') {
          itemTotal += weight * making * quantity;
        } else if (item.making_type === 'FLAT') {
          itemTotal += making * quantity;
        }
      }
      
      subtotal += itemTotal;
    });

    // Calculate advance (exclude Metal Credit from cash advance)
    const advancePaid = (order.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') return sum;
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);

    const metalAdvance = (order.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.advance_equivalent) {
        return sum + parseFloat(p.advance_equivalent);
      }
      return sum;
    }, 0);

    const metalCredit = (order.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') {
        // Check if payment has deposits array with details
        if (p.deposits && Array.isArray(p.deposits) && p.deposits.length > 0) {
          return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
        }
        // Fallback to old structure
        if (p.fine_wt) {
          return sum + parseFloat(p.fine_wt);
        }
      }
      // Old Gold should NOT be included in Metal Credit total
      return sum;
    }, 0);

    const totalAdvance = advancePaid + metalAdvance;

    return { subtotal, totalAdvance, metalCredit };
  };

  const totals = calculateTotals();

  // Group payments by mode
  const groupPaymentsByMode = () => {
    const grouped = {};
    (order.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!grouped[mode]) {
        grouped[mode] = [];
      }
      grouped[mode].push(payment);
    });
    return grouped;
  };

  const paymentsByMode = groupPaymentsByMode();

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format amount in Indian comma format (1,23,456.78)
  const formatIndianCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Convert number to words (Indian format)
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero Rupees';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const absNum = Math.abs(Math.floor(num));
    let result = '';
    
    if (absNum >= 10000000) { // Crores
      const crores = Math.floor(absNum / 10000000);
      result += convertLessThanThousand(crores) + ' Crore ';
      num = absNum % 10000000;
    } else {
      num = absNum;
    }
    
    if (num >= 100000) { // Lakhs
      const lakhs = Math.floor(num / 100000);
      result += convertLessThanThousand(lakhs) + ' Lakh ';
      num = num % 100000;
    }
    
    if (num >= 1000) { // Thousands
      const thousands = Math.floor(num / 1000);
      result += convertLessThanThousand(thousands) + ' Thousand ';
      num = num % 1000;
    }
    
    if (num > 0) {
      result += convertLessThanThousand(num);
    }
    
    return result.trim() + ' Rupees Only';
  };

  // Calculate subtotal for each payment mode
  const calculateModeSubtotal = (payments, isMetal = false) => {
    return payments.reduce((sum, p) => {
      if (isMetal) {
        // For Metal Credit, sum up fine_wt from deposits or directly
        if (p.deposits && Array.isArray(p.deposits) && p.deposits.length > 0) {
          return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
        }
        return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
      }
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);
  };

  if (!order) return null;

  return (
    <div id="thermal-print-content" className="print-content-hidden">
      {order?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{
        width: '80mm',
        padding: '2mm',
        fontFamily: "'Roboto Mono', 'Courier New', monospace",
        fontSize: '13px',
        lineHeight: '1.15',
        color: '#000',
        fontWeight: '700',
        wordWrap: 'break-word'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1px', borderBottom: '3px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '1px', textTransform: 'uppercase' }}>CUSTOMER ORDER</div>
          <div style={{ fontSize: '16px', fontWeight: '900' }}>Order ID: {order.order_id}</div>
          <div style={{ fontSize: '13px', fontWeight: '700' }}>Date: {formatDate(order.order_date || order.created_at)}</div>
        </div>

        {/* Customer Information */}
        <div style={{ marginBottom: '1px', borderBottom: '2px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '1px', textTransform: 'uppercase' }}>CUSTOMER INFORMATION</div>
          {customer && (
            <>
              <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                <strong>Name:</strong> {customer.name}
              </div>
              {customer.mobile && (
                <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                  <strong>Mobile:</strong> {customer.mobile}
                </div>
              )}
              {customer.alternate_mobile && (
                <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                  <strong>Alternate Mobile:</strong> {customer.alternate_mobile}
                </div>
              )}
              {customer.email && (
                <div style={{ fontSize: '13px', marginBottom: '2px', wordBreak: 'break-all', fontWeight: '700' }}>
                  <strong>Email:</strong> {customer.email}
                </div>
              )}
              {customer.address && (
                <div style={{ fontSize: '13px', marginBottom: '2px', wordWrap: 'break-word', fontWeight: '700' }}>
                  <strong>Address:</strong> {customer.address}
                </div>
              )}
            </>
          )}
        </div>

        {/* Rate Policy & Base Rate */}
        {(order.rate_policy || order.base24k_rate_ex_gst) && (
          <div style={{ marginBottom: '1px', borderBottom: '2px solid #000', paddingBottom: '2px' }}>
            <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '1px', textTransform: 'uppercase' }}>RATE INFORMATION</div>
            {order.rate_policy && (
              <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                <strong>Rate Policy:</strong> {
                  order.rate_policy === 'lock-today' ? 'Rate Booked Today' :
                  order.rate_policy === 'rate-at-delivery' ? 'Rate at Delivery' :
                  'None'
                }
              </div>
            )}
            {order.rate_policy === 'lock-today' && order.base24k_rate_ex_gst && (
              <>
                <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                  <strong>24K Base Rate:</strong> ₹{formatIndianCurrency(order.base24k_rate_ex_gst)}/g
                </div>
                {order.lock_date && (
                  <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                    <strong>Lock Date:</strong> {formatDate(order.lock_date)}
                  </div>
                )}
              </>
            )}
            {order.gst_applicable === false ? (
              <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                <strong>GST:</strong> Kaccha (No GST)
              </div>
            ) : (
              <div style={{ fontSize: '13px', marginBottom: '2px', fontWeight: '700' }}>
                <strong>GST:</strong> {order.gst_percent_snapshot || 3}% {order.gst_input_style === 'INCL' ? '(Inclusive)' : '(Exclusive)'}
              </div>
            )}
          </div>
        )}

        {/* Order Items */}
        <div style={{ marginBottom: '1px' }}>
          <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '2px', borderBottom: '2px solid #000', paddingBottom: '3px', textTransform: 'uppercase' }}>
            ORDER ITEMS ({order.items?.length || 0})
          </div>
          
          {order.items?.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '1px', border: '3px solid #000', padding: '2px' }}>
              {/* Item Title */}
              <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '1px', wordWrap: 'break-word', textTransform: 'uppercase' }}>
                {idx + 1}. {item.item_type || 'Item'} ({item.purity || '22K'})
              </div>

              {/* Item Images - 80% size */}
              {item.photos && item.photos.length > 0 && (
                <div style={{ marginBottom: '2px' }}>
                  {item.photos.map((photo, photoIdx) => (
                    <div key={photoIdx} style={{ marginBottom: '1px' }}>
                      <img 
                        src={photo} 
                        alt={`Item ${idx + 1} Photo ${photoIdx + 1}`}
                        style={{
                          width: '80%',
                          maxWidth: '60mm',
                          height: 'auto',
                          display: 'block',
                          margin: '0 auto',
                          border: '2px solid #000'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Item Details - Larger fonts */}
              <div style={{ fontSize: '13px', lineHeight: '1.15', fontWeight: '700' }}>
                {/* Ready badge */}
                {item.ready_item && (
                  <div style={{ marginBottom: '2px' }}>
                    <span style={{ backgroundColor: '#16a34a', color: '#fff', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '900' }}>READY STOCK</span>
                  </div>
                )}
                {/* Row 1: Weight and Quantity */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span>
                    <strong>Weight:</strong> {
                      item.weight_type === 'Fixed' 
                        ? `${item.weight_fixed}g` 
                        : `${item.weight_min}-${item.weight_max}g`
                    }
                  </span>
                  <span>
                    <strong>Quantity:</strong> {item.quantity || 1} piece{parseInt(item.quantity || 1) > 1 ? 's' : ''}
                  </span>
                </div>
                
                {/* Row 2: Purity and Size */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <span>
                    <strong>Purity:</strong> {item.purity || 'N/A'}
                  </span>
                  {item.size && (
                    <span>
                      <strong>Size:</strong> {item.size}
                    </span>
                  )}
                </div>
                
                {/* Making and Rate */}
                {item.making_value && (
                  <div style={{ marginBottom: '2px' }}>
                    <strong>Making:</strong> {
                      item.making_type === 'PERCENT' ? `${item.making_value}%` :
                      item.making_type === 'PER_GRAM' ? `₹${item.making_value}/gram` :
                      item.making_type === 'WASTAGE' ? `${item.making_value} %Wst` :
                      `₹${item.making_value}`
                    }
                  </div>
                )}
                {showItemRate && order.rate_policy === 'rate-at-delivery' ? (
                  <div style={{ marginBottom: '2px' }}>
                    <strong>Rate:</strong> At Delivery
                  </div>
                ) : showItemRate && (item.rate && parseFloat(item.rate) > 0) ? (
                  <div style={{ marginBottom: '2px' }}>
                    <strong>Rate:</strong> ₹{item.rate}/gram
                  </div>
                ) : null}
                {item.delivery_date && (
                  <div style={{ marginBottom: '2px' }}>
                    <strong>Delivery:</strong> {formatDate(item.delivery_date)}
                  </div>
                )}
                {item.public_remark && (
                  <div style={{ marginBottom: '2px', wordWrap: 'break-word' }}>
                    <strong>Remark:</strong> {item.public_remark}
                  </div>
                )}
                {item.tag_number && (
                  <div style={{ marginBottom: '2px' }}>
                    <strong>Tag:</strong> {item.tag_number}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Payments & Deposits */}
        {order.advance_payments && order.advance_payments.length > 0 && (
          <div style={{ marginBottom: '1px', borderTop: '3px solid #000', paddingTop: '2px' }}>
            <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '2px', textTransform: 'uppercase' }}>
              💳 PAYMENTS & DEPOSITS
            </div>
            
            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '1px' }}>
                <div style={{ fontSize: '13px', fontWeight: '900', marginBottom: '1px', textTransform: 'uppercase' }}>
                  {mode}
                </div>
                
                {/* Special handling for Old Gold - Box Format */}
                {mode === 'Old Gold' ? (
                  <>
                    {paymentsByMode[mode].map((payment, payIdx) => (
                      <div key={payIdx} style={{ fontSize: '16px', marginBottom: '2px', border: '3px solid #000', padding: '2px', backgroundColor: '#f9f9f9', fontWeight: '700' }}>
                        <div style={{ marginBottom: '2px' }}>
                          <strong>Date:</strong> {formatDate(payment.date)}
                        </div>
                        <div style={{ marginBottom: '2px' }}>
                          <strong>Amount:</strong> ₹{formatIndianCurrency(payment.amount || 0)}
                        </div>
                        
                        {/* Old Gold Items Breakdown - Box Format */}
                        {payment.items && payment.items.length > 0 && (
                          <div style={{ marginTop: '3px', borderTop: '1px dashed #000', paddingTop: '3px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '1px', fontSize: '16px' }}>Item Details:</div>
                            {payment.items.map((item, itemIdx) => (
                              <div key={itemIdx} style={{ marginBottom: '2px', padding: '3px', border: '1px solid #000', backgroundColor: '#fff' }}>
                                {item.item_name && (
                                  <div style={{ fontSize: '16px', marginBottom: '2px' }}><strong>Item:</strong> {item.item_name}</div>
                                )}
                                <table style={{ width: '100%', fontSize: '16px' }}>
                                  <tbody>
                                    {item.gross_wt && (
                                      <tr>
                                        <td style={{ padding: '1px 0' }}><strong>Gross Weight:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right' }}>{item.gross_wt}g</td>
                                      </tr>
                                    )}
                                    {item.less_wt && (
                                      <tr>
                                        <td style={{ padding: '1px 0' }}><strong>Less Weight:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right' }}>{item.less_wt}g</td>
                                      </tr>
                                    )}
                                    {item.net_wt && (
                                      <tr>
                                        <td style={{ padding: '1px 0' }}><strong>Net Weight:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right' }}>{item.net_wt}g</td>
                                      </tr>
                                    )}
                                    {item.purity && (
                                      <tr>
                                        <td style={{ padding: '1px 0' }}><strong>Purity:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right' }}>{item.purity}%</td>
                                      </tr>
                                    )}
                                    {item.fine_wt && (
                                      <tr style={{ fontWeight: 'bold' }}>
                                        <td style={{ padding: '1px 0', borderTop: '1px solid #000' }}><strong>Fine Weight:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right', borderTop: '1px solid #000' }}>{item.fine_wt}g</td>
                                      </tr>
                                    )}
                                    {showItemRate && (item.purchase_rate || item.rate) && (
                                      <tr>
                                        <td style={{ padding: '1px 0' }}><strong>Rate:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right' }}>₹{formatIndianCurrency(item.purchase_rate || item.rate)}/gram</td>
                                      </tr>
                                    )}
                                    {(item.line_total || item.value) && (
                                      <tr style={{ fontWeight: 'bold' }}>
                                        <td style={{ padding: '1px 0', borderTop: '1px solid #000' }}><strong>Amount:</strong></td>
                                        <td style={{ padding: '1px 0', textAlign: 'right', borderTop: '1px solid #000' }}>₹{formatIndianCurrency(item.line_total || item.value)}</td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {payment.note && (
                          <div style={{ marginTop: '3px', fontSize: '16px', wordWrap: 'break-word' }}>
                            <strong>Note:</strong> {payment.note}
                          </div>
                        )}
                      </div>
                    ))}
                    {/* Old Gold Subtotal */}
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: 'bold', 
                      textAlign: 'right', 
                      marginBottom: '1px',
                      paddingTop: '3px',
                      borderTop: '2px solid #000'
                    }}>
                      Old Gold Total: ₹{formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}
                    </div>
                  </>
                ) : mode === 'Metal Credit' ? (
                  /* Metal Credit - COMPACT Format */
                  <>
                    {paymentsByMode[mode]
                      .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
                      .map((payment, payIdx) => {
                        const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
                        
                        return (
                          <div key={payIdx} style={{ fontSize: '16px', marginBottom: '2px', border: '3px solid #000', padding: '3px', backgroundColor: '#f9f9f9', fontWeight: '700' }}>
                            {hasDeposits ? (
                              <>
                                {payment.deposits.map((deposit, depIdx) => (
                                  <div key={depIdx} style={{ marginBottom: '1px', paddingBottom: '3px', borderBottom: depIdx < payment.deposits.length - 1 ? '2px dashed #000' : 'none' }}>
                                    <div style={{ fontWeight: '900', fontSize: '13px', marginBottom: '2px', textTransform: 'uppercase' }}>
                                      Deposit {depIdx + 1}{deposit.item_name && `: ${deposit.item_name}`}
                                    </div>
                                    
                                    {/* Compact list format instead of table */}
                                    <div style={{ fontSize: '16px', lineHeight: '1.15', fontWeight: '700' }}>
                                      <div><strong>Date:</strong> {formatDate(deposit.date || payment.date)}</div>
                                      {(deposit.type || deposit.type_name_other) && (
                                        <div><strong>Type:</strong> {deposit.type_name_other || deposit.type}</div>
                                      )}
                                      <div><strong>Gross Weight:</strong> {deposit.gross_wt || deposit.net_wt || 0}g{deposit.less_wt ? ` | Less: ${deposit.less_wt}g` : ''}</div>
                                      <div><strong>Net Weight:</strong> {deposit.net_wt || 0}g | <strong>Purity:</strong> {deposit.purity_label || `${deposit.purity}%`}</div>
                                      <div style={{ fontSize: '13px', fontWeight: '900', marginTop: '2px' }}><strong>Fine Weight:</strong> {deposit.fine_wt || 0}g</div>
                                    </div>
                                    
                                    {deposit.reference && (
                                      <div style={{ marginTop: '2px', wordWrap: 'break-word', fontSize: '16px', fontWeight: '700' }}>
                                        <strong>Reference:</strong> {deposit.reference}
                                      </div>
                                    )}
                                    
                                    {deposit.photos && deposit.photos.length > 0 && (
                                      <div style={{ marginTop: '2px' }}>
                                        <div style={{ fontSize: '16px', fontWeight: '900', marginBottom: '2px' }}>Photos: {deposit.photos.length}</div>
                                        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
                                          {deposit.photos.slice(0, 2).map((photo, photoIdx) => {
                                            const photoUrl = typeof photo === 'string' ? photo : (photo.edited || photo.original);
                                            return photoUrl ? (
                                              <img 
                                                key={photoIdx}
                                                src={photoUrl} 
                                                alt={`Photo ${photoIdx + 1}`}
                                                style={{ 
                                                  width: '35mm', 
                                                  height: '30px', 
                                                  objectFit: 'cover', 
                                                  border: '2px solid #000',
                                                  display: 'block'
                                                }}
                                              />
                                            ) : null;
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            ) : (
                              /* Single Payment without deposits */
                              <div style={{ fontWeight: '700' }}>
                                <div><strong>Date:</strong> {formatDate(payment.date)}</div>
                                <div><strong>Fine Weight:</strong> {payment.fine_wt || payment.fine_grams || 0}g</div>
                                {payment.note && (
                                  <div style={{ marginTop: '2px', wordWrap: 'break-word' }}>
                                    <strong>Note:</strong> {payment.note}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {/* Metal Credit Subtotal */}
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '900', 
                      textAlign: 'right', 
                      marginBottom: '1px',
                      paddingTop: '3px',
                      borderTop: '3px solid #000'
                    }}>
                      Metal Credit Total: {calculateModeSubtotal(paymentsByMode[mode], true).toFixed(3)}g
                    </div>
                  </>
                ) : (
                  /* Regular payments - Box Format */
                  <>
                    <div style={{ marginBottom: '2px', border: '3px solid #000', padding: '3px', backgroundColor: '#f9f9f9' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '16px', fontWeight: '700' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #000' }}>
                            <th style={{ padding: '2px', textAlign: 'left', borderRight: '1px solid #000', fontWeight: '900' }}>Date</th>
                            <th style={{ padding: '2px', textAlign: 'right', borderRight: '1px solid #000', fontWeight: '900' }}>Amount</th>
                            <th style={{ padding: '2px', textAlign: 'left', fontWeight: '900' }}>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsByMode[mode].map((payment, payIdx) => {
                            const amount = parseFloat(payment.amount) || 0;
                            const isRefund = payment.transaction_type === 'REFUND';
                            const displayAmount = isRefund ? -amount : amount;
                            
                            return (
                              <tr key={payIdx} style={{ borderBottom: payIdx < paymentsByMode[mode].length - 1 ? '1px solid #000' : 'none' }}>
                                <td style={{ padding: '2px', borderRight: '1px solid #000', fontSize: '16px', fontWeight: '700' }}>
                                  {formatDate(payment.date)}
                                </td>
                                <td style={{ padding: '2px', textAlign: 'right', borderRight: '1px solid #000', fontSize: '16px', color: isRefund ? '#d00' : '#000', fontWeight: '900' }}>
                                  {isRefund ? '-' : ''}₹{formatIndianCurrency(Math.abs(displayAmount))}
                                </td>
                                <td style={{ padding: '2px', fontSize: '16px', wordWrap: 'break-word', fontWeight: '700' }}>
                                  {isRefund && <div style={{ color: '#d00', fontWeight: '900' }}>REFUND</div>}
                                  {payment.mode === 'UPI' && payment.upi_ref && (
                                    <div>Reference: {payment.upi_ref}</div>
                                  )}
                                  {payment.mode === 'Card' && payment.card_last4 && (
                                    <div>Card: ****{payment.card_last4}</div>
                                  )}
                                  {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref && (
                                    <div>UTR: {payment.utr_ref}</div>
                                  )}
                                  {payment.mode === 'Cheque' && payment.cheque_no && (
                                    <div>Cheque: #{payment.cheque_no}</div>
                                  )}
                                  {payment.note && (
                                    <div style={{ fontSize: '16px' }}>{payment.note}</div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Mode Subtotal */}
                    <div style={{ 
                      fontSize: '13px', 
                      fontWeight: '900', 
                      textAlign: 'right', 
                      marginBottom: '1px',
                      paddingTop: '3px',
                      borderTop: '3px solid #000'
                    }}>
                      {mode} Total: ₹{formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}
                    </div>
                  </>
                )}
              </div>
            ))}
            
            {/* Net Advance Total - Big Boxed */}
            <div style={{ 
              marginTop: '2px', 
              border: '3px double #000'
            }}>
              {/* Net Advance Paid Section */}
              <div style={{
                padding: '2px',
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                borderBottom: totals.metalCredit > 0 ? '2px solid #000' : 'none'
              }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>
                  NET ADVANCE PAID
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>
                  ₹{formatIndianCurrency(totals.totalAdvance)}
                </div>
                <div style={{ fontSize: '8px', fontStyle: 'italic', wordWrap: 'break-word' }}>
                  ({numberToWords(totals.totalAdvance)})
                </div>
              </div>
              
              {/* Metal Credit Section - Only if exists */}
              {totals.metalCredit > 0 && (
                <div style={{
                  padding: '2px',
                  textAlign: 'center',
                  backgroundColor: '#fff9e6'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                    METAL CREDIT
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {totals.metalCredit.toFixed(2)}g fine gold
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signatures - Optimized */}
        {(order.management_signature || order.customer_signature) && (
          <div style={{ marginBottom: '2px', borderTop: '2px solid #000', paddingTop: '2px' }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1px' }}>✍️ SIGNATURES</div>
            
            {order.management_signature && (
              <div style={{ marginBottom: '2px' }}>
                <div style={{ fontSize: '8px', fontWeight: 'bold', marginBottom: '1px' }}>Management</div>
                <div style={{ border: '1px solid #000', padding: '1px', marginBottom: '1px' }}>
                  <img 
                    src={order.management_signature} 
                    alt="Management Signature"
                    style={{
                      width: '55mm',
                      height: 'auto',
                      maxHeight: '15mm',
                      display: 'block'
                    }}
                  />
                </div>
                {order.management_signature_name && (
                  <div style={{ fontSize: '7px' }}>Name: {order.management_signature_name}</div>
                )}
              </div>
            )}

            {order.customer_signature && (
              <div style={{ marginBottom: '2px' }}>
                <div style={{ fontSize: '8px', fontWeight: 'bold', marginBottom: '1px' }}>Customer</div>
                <div style={{ border: '1px solid #000', padding: '1px', marginBottom: '1px' }}>
                  <img 
                    src={order.customer_signature} 
                    alt="Customer Signature"
                    style={{
                      width: '55mm',
                      height: 'auto',
                      maxHeight: '15mm',
                      display: 'block'
                    }}
                  />
                </div>
                {customer && customer.name && (
                  <div style={{ fontSize: '7px' }}>Name: {customer.name}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Terms & Conditions - Optimized */}
        <div style={{ marginBottom: '2px', borderTop: '2px solid #000', paddingTop: '2px' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1px' }}>📋 TERMS & CONDITIONS</div>
          {termsAndConditions.length > 0 ? (
            <div style={{ fontSize: '7px', lineHeight: '1.15' }}>
              {termsAndConditions.map((term, idx) => (
                <div key={idx} style={{ marginBottom: '2px', wordWrap: 'break-word' }}>
                  • {term}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '7px', fontStyle: 'italic', color: '#666' }}>
              No terms and conditions configured.
            </div>
          )}
        </div>

        {/* Footer Spacing - Optimized */}
        <div style={{ height: '6mm' }}></div>

      </div>
    </div>
  );
};

export default OrderPrintThermal;
